import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import * as XLSX from "xlsx"; // Import the xlsx library
import Select from "react-select"; // Import react-select for searchable dropdown
import ScoreCard from "./components/ScoreCard"; // Import the new ScoreCard component

// Import Ant Design components and styles
import { DatePicker } from "antd";

import "antd/dist/reset.css"; // Import Ant Design styles

// Add CSS for animations
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ScoreTable = (props) => {
  // Add style element for animations
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = animationStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // State variables for total records, pagination, search and loading state.
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(9); // Changed to 9 for a 3x3 grid
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [gameIdFilter, setGameIdFilter] = useState(""); // State for GameID filter
  const [listTop, setListTop] = useState("1");
  const [gamesList, setGamesList] = useState([]); // State to store available games
  // New state for score detail modal
  const [selectedScore, setSelectedScore] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // New state for date range filter
  // Replace the dateRange state with startDate and endDate
  const [dateRange, setDateRange] = useState([null, null]);

  function getStartPosition(pageNumber, recordsPerPage) {
    return (pageNumber - 1) * recordsPerPage;
  }

  // Fetch available games for the filter dropdown
  const fetchGames = useCallback(async () => {
    try {
      const response = await api.get("/api/game/gametable.php");
      if (response.data && Array.isArray(response.data.data)) {
        setGamesList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching games:", error);
    }
  }, []);

  // Function to handle date range reset
  const handleDateRangeReset = () => {
    setDateRange([null, null]);
    setPage(1); // Reset to first page when filter changes
  };

  // In your fetchScores function, modify the date range parameters section
  const fetchScores = useCallback(async () => {
    setLoading(true);
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append("start", getStartPosition(page, perPage));
    queryParams.append("length", perPage);
    queryParams.append("search[value]", search);
    queryParams.append("gameid", gameIdFilter);
    queryParams.append("list_top", listTop);

    // Add date range parameters if they exist
    // Check if dateRange is not null before accessing its elements
    if (dateRange && dateRange[0]) {
      queryParams.append("start_date", dateRange[0].format("YYYY-MM-DD"));
    }
    if (dateRange && dateRange[1]) {
      queryParams.append("end_date", dateRange[1].format("YYYY-MM-DD"));
    }

    try {
      const response = await api.get(
        `/api/score/scoretable.php?${queryParams}`
      );
      console.log("Response:", response.data);
      setData(Array.isArray(response.data.data) ? response.data.data : []);
      setTotalRows(response.data.recordsFiltered);
    } catch (error) {
      console.error("Error fetching scores:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, gameIdFilter, listTop, dateRange]);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  useEffect(() => {
    // Fetch games for the filter dropdown when component mounts
    fetchGames();
  }, [fetchGames]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle game filter change - No longer needed as we handle it directly in the onChange of Select
  // We can keep it for reference or remove it

  const handleFilter = (e) => {
    setListTop(e.target.checked ? "1" : "0");
    setPage(1); // Reset to first page when filter changes
  };
  // Function to handle Excel export
  const handleExport = () => {
    // Create a copy of the data for export
    const exportData = data.map((row) => {
      // Format dates for export
      const startTime = new Date(row.StartTime + "Z");
      const endTime = new Date(row.EndTime + "Z");

      // Calculate time taken
      const timeDiffMs = endTime - startTime;
      const seconds = Math.floor(timeDiffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      const timeTaken = `${minutes}:${remainingSeconds
        .toString()
        .padStart(2, "0")}`;

      return {
        Name: row.Name,
        "Mobile No": row.MobileNo,
        "Game ID": row.GameID,
        "Start Time": startTime.toLocaleString(),
        "End Time": endTime.toLocaleString(),
        "Correct Words": row.CorrectWords,
        "Correct Sentences": row.CorrectSentence,
        "Time Taken": timeTaken,
        "Remaining Time": row.ReminingTime,
      };
    });

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Scores");

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, "scores_export.xlsx");
  };

  // Function to open score detail modal
  const openScoreDetail = (score) => {
    setSelectedScore(score);
    setShowDetailModal(true);
  };

  // Function to close score detail modal
  const closeScoreDetail = () => {
    setShowDetailModal(false);
    setSelectedScore(null);
  };

  // Function to render a score card

  // Score Detail Modal Component
  const ScoreDetailModal = ({ score, onClose }) => {
    if (!score) return null;

    // Format dates
    const startTime = new Date(score.StartTime + "Z");
    const endTime = new Date(score.EndTime + "Z");

    // Calculate time taken
    const timeDiffMs = endTime - startTime;
    const seconds = Math.floor(timeDiffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const timeTaken = `${minutes}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;

    // Parse ScoreDetails if available
    let scoreDetails = [];
    try {
      if (score.ScoreDetails) {
        scoreDetails = JSON.parse(score.ScoreDetails);
      }
    } catch (error) {
      console.error("Error parsing ScoreDetails:", error);
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Score Details - {score.Name}</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">
                  User Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Name:</span>
                    <span>{score.Name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Mobile:</span>
                    <span>{score.MobileNo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Game ID:</span>
                    <span>{score.GameID}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Game Name:</span>
                    <span>{score.GameName}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">
                  Score Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Correct Words:</span>
                    <span className="font-mono">{score.CorrectWords}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Correct Sentences:</span>
                    <span className="font-mono">{score.CorrectSentence}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Time Taken:</span>
                    <span className="font-mono">{timeTaken} Sec</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Game Duration:</span>
                    <span className="font-mono">{score.duration} Sec</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">
                Time Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium">Start Time:</span>
                  <div className="mt-1">{startTime.toLocaleString()}</div>
                </div>
                <div>
                  <span className="font-medium">End Time:</span>
                  <div className="mt-1">{endTime.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {scoreDetails.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">
                  Detailed Score Information
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Correct Kural
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Your Arrangement
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Result
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {scoreDetails.map((detail, index) => (
                        <tr
                          key={index}
                          className={
                            detail.isCorrect ? "bg-green-50" : "bg-red-50"
                          }
                        >
                          <td className="px-4 py-2 text-sm">
                            {detail.correct_kural && (
                              <>
                                <div>
                                  {detail.correct_kural
                                    .split(" ")
                                    .slice(0, 4)
                                    .join(" ")}
                                </div>
                                <div>
                                  {detail.correct_kural
                                    .split(" ")
                                    .slice(4)
                                    .join(" ")}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {detail.your_arrangement && (
                              <>
                                <div>
                                  {detail.your_arrangement
                                    .split(" ")
                                    .slice(0, 4)
                                    .join(" ")}
                                </div>
                                <div>
                                  {detail.your_arrangement
                                    .split(" ")
                                    .slice(4)
                                    .join(" ")}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {detail.isCorrect ? (
                              <span className="text-green-600 font-medium">
                                Correct
                              </span>
                            ) : (
                              <span className="text-red-600 font-medium">
                                Incorrect
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(!scoreDetails || scoreDetails.length === 0) && (
              <div className="text-center py-4 text-gray-500">
                No detailed score information available
              </div>
            )}
          </div>

          <div className="bg-gray-100 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Custom pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalRows / perPage);
    const pageNumbers = [];

    // Calculate which page numbers to show
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, page + 2);

    // Ensure we always show 5 pages if possible
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(5, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - 4);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6 mb-4">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
            className={`px-3 py-1 rounded ${
              page === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className={`px-3 py-1 rounded ${
              page === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            ‹
          </button>

          {pageNumbers.map((num) => (
            <button
              key={num}
              onClick={() => handlePageChange(num)}
              className={`px-3 py-1 rounded ${
                page === num
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {num}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className={`px-3 py-1 rounded ${
              page === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
            className={`px-3 py-1 rounded ${
              page === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            »
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div>
      {/* Filters row - Modified to include all three filters in one line */}
      <div className="mb-4 flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
        {/* Game ID Filter */}
        <div className="w-full md:w-1/4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Game
          </label>
          <Select
            className="basic-single"
            classNamePrefix="select"
            value={
              gamesList.find((game) => game.id === gameIdFilter)
                ? {
                    value: gameIdFilter,
                    label: gamesList.find((game) => game.id === gameIdFilter)
                      .gameName,
                  }
                : { value: "0", label: "All Games" }
            }
            onChange={(selectedOption) => {
              setGameIdFilter(selectedOption.value);
              setPage(1); // Reset to first page when filter changes
            }}
            options={[
              { value: "0", label: "All Games" },
              ...gamesList.map((game) => ({
                value: game.id,
                label: game.gameName,
              })),
            ]}
            placeholder="Search for a game..."
            isClearable={false}
            isSearchable={true}
            styles={{
              control: (base) => ({
                ...base,
                padding: "0.25rem",
                borderColor: "#d1d5db", // Tailwind border-gray-300
                borderRadius: "0.375rem", // Tailwind rounded-md
                backgroundColor: "#fff", // Tailwind bg-white
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#6366f1", // Tailwind indigo-500
                },
              }),
              menu: (base) => ({
                ...base,
                marginTop: "0.25rem",
                borderRadius: "0.375rem", // Tailwind rounded-md
                overflow: "hidden",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#6366f1"
                  : state.isFocused
                  ? "#e0e7ff"
                  : "#fff",
                color: state.isSelected ? "#fff" : "#111827",
                "&:hover": {
                  backgroundColor: state.isSelected ? "#6366f1" : "#e0e7ff",
                },
              }),
            }}
          />
        </div>

        {/* Date Range Filter - Moved to the same line */}
        <div className="w-full md:w-1/3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range Filter
          </label>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => {
              // Set to empty array instead of null when cleared
              setDateRange(dates || [null, null]);
              setPage(1); // Reset to first page when filter changes
            }}
            format="YYYY-MM-DD"
            allowClear={true}
            style={{ width: "100%" }}
            className="w-full p-2"
          />
        </div>

        {/* Search input */}
        <div className="w-full md:w-5/12">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Search scores..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Reset Date Filter button - moved to a more appropriate location */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={handleDateRangeReset}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          disabled={!dateRange[0] && !dateRange[1]}
        >
          Reset Date Filter
        </button>
      </div>

      {/* Export Button and Filter Checkbox */}
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={handleExport}
          className="hidden md:block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        >
          Export to Excel
        </button>
        <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg shadow-md border border-gray-300 transition-all duration-200 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only" /* Hide the actual checkbox but keep it functional */
              checked={listTop === "1"}
              onChange={handleFilter}
            />
            <div
              className={`w-7 h-7 flex items-center justify-center rounded-md border-2 ${
                listTop === "1"
                  ? "bg-blue-600 border-blue-700"
                  : "bg-white border-gray-400"
              }`}
            >
              {listTop === "1" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
          <span className="ml-3 font-medium text-gray-800 text-lg">
            List by Top Scores
          </span>
        </label>
      </div>

      {/* Loading indicator */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.length > 0 ? (
              data.map((score, index) => (
                <ScoreCard
                  key={score.id}
                  score={score}
                  index={index}
                  openScoreDetail={openScoreDetail}
                />
              ))
            ) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                No scores found
              </div>
            )}
          </div>

          {/* Pagination */}
          {data.length > 0 && <Pagination />}

          {/* Records info and per page selector */}
          <div className="flex flex-col md:flex-row justify-between items-center mt-2 text-sm text-gray-600">
            <div className="mb-2 md:mb-0">
              Showing {getStartPosition(page, perPage) + 1} to{" "}
              {Math.min(getStartPosition(page, perPage) + perPage, totalRows)}{" "}
              of {totalRows} records
            </div>
            <div className="flex items-center">
              <span className="mr-2">Records per page:</span>
              <select
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1); // Reset to first page when changing records per page
                }}
              >
                <option value={9}>9</option>
                <option value={18}>18</option>
                <option value={27}>27</option>
                <option value={36}>36</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* Score Detail Modal */}
      {showDetailModal && selectedScore && (
        <ScoreDetailModal score={selectedScore} onClose={closeScoreDetail} />
      )}
    </div>
  );
};

export default ScoreTable;
